import { supabase } from '@/integrations/supabase/client';

const supabaseAny = supabase as any;

const logCampaignPromotersWarning = (event: string, details: Record<string, unknown>) => {
  console.warn('[campaign-promoters]', {
    event,
    ...details,
  });
};

export const campaignPromoterCommittedStatuses = [
  'accepted',
  'training_required',
  'ready',
  'checked_in',
  'completed',
] as const;

export const campaignPromoterLinkedStatuses = [
  'invited',
  ...campaignPromoterCommittedStatuses,
] as const;

export type CampaignPromoterStatus = 'accepted' | 'invited' | 'declined' | 'active' | 'completed';

export type CampaignPromoterSource = 'shift' | 'campaign' | 'shift_and_campaign';

export interface CampaignPromoterRow {
  campaign_id: string;
  org_id: string;
  ba_id: string;
  user_id: string;
  status: string;
  role: string | null;
  shift_count: number;
  invited_count: number;
  committed_count: number;
  client_approved_count: number;
  has_shift_link: boolean;
  has_campaign_assignment: boolean;
  source: string;
  campaign_assignment_id: string | null;
  campaign_assignment_status: string | null;
  full_name: string | null;
  avatar_url: string | null;
  email: string | null;
  photo_url: string | null;
  latitude: number | null;
  longitude: number | null;
  locality: string | null;
  admin_area: string | null;
}

export interface CampaignPromoter extends CampaignPromoterRow {
  status: CampaignPromoterStatus;
  source: CampaignPromoterSource;
}

type PromoterProfileRow = {
  ba_id: string;
  photo_url: string | null;
  latitude: number | null;
  longitude: number | null;
  locality: string | null;
  admin_area: string | null;
};

const statusPriority: Record<CampaignPromoterStatus, number> = {
  declined: 0,
  invited: 1,
  accepted: 2,
  completed: 3,
  active: 4,
};

export const normalizeCampaignPromoterStatus = (
  status: string | null | undefined,
): CampaignPromoterStatus => {
  // Map all committed pre-shift statuses to 'accepted' for display purposes
  if (['accepted', 'training_required', 'ready'].includes(status ?? '')) return 'accepted';
  if (status === 'checked_in') return 'active';
  if (status === 'completed') return 'completed';
  if (status === 'invited') return 'invited';
  // declined, cancelled, no_show, null → declined
  return 'declined';
};

export const normalizeCampaignPromoterSource = (
  hasShiftLink: boolean,
  hasCampaignAssignment: boolean,
): CampaignPromoterSource => {
  if (hasShiftLink && hasCampaignAssignment) return 'shift_and_campaign';
  if (hasShiftLink) return 'shift';
  return 'campaign';
};

export const mergeCampaignPromoterRows = (rows: CampaignPromoterRow[]): CampaignPromoter[] => {
  const merged = new Map<string, CampaignPromoter>();

  rows.forEach((row) => {
    const baseStatus = normalizeCampaignPromoterStatus(row.status);
    // Do NOT downgrade to 'invited' when client approval is pending — the BA already
    // accepted. Surface a separate pending_approval badge in the UI instead.
    const normalizedStatus = baseStatus;

    const promoter: CampaignPromoter = {
      ...row,
      status: normalizedStatus,
      role: row.role ?? 'brand_ambassador',
      source: normalizeCampaignPromoterSource(row.has_shift_link, row.has_campaign_assignment),
      shift_count: Number(row.shift_count || 0),
      invited_count: Number(row.invited_count || 0),
      committed_count: Number(row.committed_count || 0),
      client_approved_count: Number(row.client_approved_count || 0),
    };

    const existing = merged.get(promoter.ba_id);
    if (!existing) {
      merged.set(promoter.ba_id, promoter);
      return;
    }

    const winner =
      statusPriority[promoter.status] > statusPriority[existing.status] ? promoter : existing;
    const fallback = winner === promoter ? existing : promoter;

    const hasShiftLink = existing.has_shift_link || promoter.has_shift_link;
    const hasCampaignAssignment =
      existing.has_campaign_assignment || promoter.has_campaign_assignment;

    merged.set(promoter.ba_id, {
      ...fallback,
      ...winner,
      has_shift_link: hasShiftLink,
      has_campaign_assignment: hasCampaignAssignment,
      source: normalizeCampaignPromoterSource(hasShiftLink, hasCampaignAssignment),
      role: winner.role || fallback.role || 'brand_ambassador',
      shift_count: Math.max(existing.shift_count || 0, promoter.shift_count || 0),
      invited_count: Math.max(existing.invited_count || 0, promoter.invited_count || 0),
      committed_count: Math.max(existing.committed_count || 0, promoter.committed_count || 0),
      client_approved_count: Math.max(
        existing.client_approved_count || 0,
        promoter.client_approved_count || 0,
      ),
      campaign_assignment_id:
        winner.campaign_assignment_id ?? fallback.campaign_assignment_id ?? null,
      campaign_assignment_status:
        winner.campaign_assignment_status ?? fallback.campaign_assignment_status ?? null,
    });
  });

  return Array.from(merged.values()).sort((a, b) => {
    const aName = a.full_name || '';
    const bName = b.full_name || '';
    return aName.localeCompare(bName);
  });
};

const buildFallbackShiftRows = (shiftAssignments: any[], orgId: string) => {
  const grouped = new Map<
    string,
    {
      campaign_id: string;
      org_id: string;
      ba_id: string;
      shift_count: number;
      invited_count: number;
      committed_count: number;
      client_approved_count: number;
      latest_role: string | null;
      latest_starts_at: string | null;
      linked_statuses: Set<string>;
    }
  >();

  (shiftAssignments || []).forEach((assignment: any) => {
    const baId = assignment?.ba_id;
    const campaignId = assignment?.shift?.campaign_id;
    if (!baId || !campaignId) return;

    const current =
      grouped.get(baId) ||
      {
        campaign_id: campaignId,
        org_id: orgId,
        ba_id: baId,
        shift_count: 0,
        invited_count: 0,
        committed_count: 0,
        client_approved_count: 0,
        latest_role: null,
        latest_starts_at: null,
        linked_statuses: new Set<string>(),
      };

    current.shift_count += 1;
    const status = String(assignment?.status || '');
    current.linked_statuses.add(status);

    if (status === 'invited') current.invited_count += 1;
    if (campaignPromoterCommittedStatuses.includes(status as any)) current.committed_count += 1;
    if (assignment?.client_status === 'approved') current.client_approved_count += 1;

    const startsAt = assignment?.shift?.starts_at ?? null;
    if (
      startsAt &&
      (!current.latest_starts_at || new Date(startsAt).getTime() > new Date(current.latest_starts_at).getTime())
    ) {
      current.latest_starts_at = startsAt;
      current.latest_role = assignment?.shift?.role ?? current.latest_role;
    }

    grouped.set(baId, current);
  });

  return Array.from(grouped.values()).map((row) => {
    let status = 'declined';
    if (row.committed_count > 0) status = 'accepted';
    else if (row.linked_statuses.has('invited')) status = 'invited';

    return {
      campaign_id: row.campaign_id,
      org_id: row.org_id,
      ba_id: row.ba_id,
      user_id: '',
      status,
      role: row.latest_role ?? 'brand_ambassador',
      shift_count: row.shift_count,
      invited_count: row.invited_count,
      committed_count: row.committed_count,
      client_approved_count: row.client_approved_count,
      has_shift_link: true,
      has_campaign_assignment: false,
      source: 'shift',
      campaign_assignment_id: null,
      campaign_assignment_status: null,
      full_name: null,
      avatar_url: null,
      email: null,
      photo_url: null,
      latitude: null,
      longitude: null,
      locality: null,
      admin_area: null,
    } satisfies CampaignPromoterRow;
  });
};

const buildFallbackCampaignAssignmentRows = (campaignAssignments: any[]) => {
  return (campaignAssignments || []).map((assignment: any) => ({
    campaign_id: assignment.campaign_id,
    org_id: assignment.org_id,
    ba_id: assignment.ba_id,
    user_id: '',
    status: String(assignment.status || 'invited'),
    role: assignment.role ?? 'brand_ambassador',
    shift_count: 0,
    invited_count: assignment.status === 'invited' ? 1 : 0,
    committed_count: ['accepted', 'active', 'completed'].includes(assignment.status) ? 1 : 0,
    client_approved_count: 0,
    has_shift_link: false,
    has_campaign_assignment: true,
    source: 'campaign',
    campaign_assignment_id: assignment.id,
    campaign_assignment_status: assignment.status,
    full_name: null,
    avatar_url: null,
    email: null,
    photo_url: null,
    latitude: null,
    longitude: null,
    locality: null,
    admin_area: null,
  })) satisfies CampaignPromoterRow[];
};

const enrichCampaignPromoterRows = async (
  rows: CampaignPromoterRow[],
): Promise<CampaignPromoterRow[]> => {
  const baIds = Array.from(new Set(rows.map((row) => row.ba_id).filter(Boolean)));
  if (baIds.length === 0) return rows;

  const { data: bas, error: basError } = await supabaseAny
    .from('bas')
    .select('id, user_id')
    .in('id', baIds);

  if (basError) {
    logCampaignPromotersWarning('fallback_enrichment_bas_failed', {
      ba_count: baIds.length,
      code: basError.code ?? null,
      message: basError.message ?? 'unknown',
    });
    return rows;
  }

  const userIds = (bas || []).map((ba: any) => ba.user_id).filter(Boolean);
  const [profilesResult, baProfilesResult] = await Promise.all([
    userIds.length > 0
      ? supabaseAny.from('profiles').select('id, full_name, avatar_url, email').in('id', userIds)
      : Promise.resolve({ data: [], error: null }),
    supabaseAny
      .from('ba_profiles')
      .select('ba_id, photo_url, latitude, longitude, locality, admin_area')
      .in('ba_id', baIds),
  ]);

  if (profilesResult.error) {
    logCampaignPromotersWarning('fallback_enrichment_profiles_failed', {
      user_count: userIds.length,
      code: profilesResult.error.code ?? null,
      message: profilesResult.error.message ?? 'unknown',
    });
  }

  if (baProfilesResult.error) {
    logCampaignPromotersWarning('fallback_enrichment_ba_profiles_failed', {
      ba_count: baIds.length,
      code: baProfilesResult.error.code ?? null,
      message: baProfilesResult.error.message ?? 'unknown',
    });
  }

  const baById = new Map((bas || []).map((ba: any) => [ba.id, ba]));
  const profileByUserId = new Map(
    ((profilesResult.error ? [] : profilesResult.data) || []).map((profile: any) => [profile.id, profile]),
  );
  const baProfileByBaId = new Map(
    ((baProfilesResult.error ? [] : baProfilesResult.data) || []).map(
      (profile: PromoterProfileRow) => [profile.ba_id, profile],
    ),
  );

  return rows.map((row) => {
    const ba = baById.get(row.ba_id);
    const profile = ba ? profileByUserId.get(ba.user_id) : null;
    const baProfile = baProfileByBaId.get(row.ba_id);

    return {
      ...row,
      user_id: row.user_id || ba?.user_id || '',
      full_name: row.full_name || profile?.full_name || null,
      avatar_url: row.avatar_url || profile?.avatar_url || null,
      email: row.email || profile?.email || null,
      photo_url: row.photo_url || baProfile?.photo_url || null,
      latitude: row.latitude ?? baProfile?.latitude ?? null,
      longitude: row.longitude ?? baProfile?.longitude ?? null,
      locality: row.locality || baProfile?.locality || null,
      admin_area: row.admin_area || baProfile?.admin_area || null,
    };
  });
};

const fetchCampaignPromotersFallback = async (
  campaignId: string,
  options?: { includeInvited?: boolean },
) => {
  const includeInvited = options?.includeInvited ?? true;
  const linkedStatuses = includeInvited
    ? [...campaignPromoterLinkedStatuses, 'declined', 'cancelled', 'no_show']
    : [...campaignPromoterCommittedStatuses];
  const assignmentStatuses = includeInvited
    ? ['invited', 'accepted', 'active', 'completed', 'declined']
    : ['accepted', 'active', 'completed'];

  const { data: campaign, error: campaignError } = await supabaseAny
    .from('campaigns')
    .select('id, org_id')
    .eq('id', campaignId)
    .maybeSingle();

  if (campaignError) {
    logCampaignPromotersWarning('fallback_campaign_lookup_failed', {
      campaign_id: campaignId,
      code: campaignError.code ?? null,
      message: campaignError.message ?? 'unknown',
    });
  }

  const orgId = campaign?.org_id ?? '';

  const { data: campaignShifts, error: campaignShiftsError } = await supabaseAny
    .from('campaign_shifts')
    .select('id, campaign_id, role, starts_at')
    .eq('campaign_id', campaignId);

  if (campaignShiftsError) {
    logCampaignPromotersWarning('fallback_campaign_shifts_failed', {
      campaign_id: campaignId,
      code: campaignShiftsError.code ?? null,
      message: campaignShiftsError.message ?? 'unknown',
    });
  }

  const shiftIds = (campaignShiftsError ? [] : campaignShifts || [])
    .map((shift: any) => shift.id)
    .filter(Boolean);
  const shiftById = new Map(
    (campaignShiftsError ? [] : campaignShifts || []).map((shift: any) => [shift.id, shift]),
  );

  const shiftAssignmentsResult =
    shiftIds.length > 0
      ? await supabaseAny
          .from('shift_assignments')
          .select('ba_id, status, client_status, shift_id, invited_at, responded_at')
          .in('shift_id', shiftIds)
          .in('status', linkedStatuses)
      : { data: [], error: null };

  if (shiftAssignmentsResult.error) {
    logCampaignPromotersWarning('fallback_shift_assignments_failed', {
      campaign_id: campaignId,
      shift_count: shiftIds.length,
      code: shiftAssignmentsResult.error.code ?? null,
      message: shiftAssignmentsResult.error.message ?? 'unknown',
    });
  }

  const campaignAssignmentsResult = await supabaseAny
    .from('campaign_assignments')
    .select('id, campaign_id, org_id, ba_id, status, role, invited_at, accepted_at')
    .eq('campaign_id', campaignId)
    .in('status', assignmentStatuses);

  if (campaignAssignmentsResult.error) {
    logCampaignPromotersWarning('fallback_campaign_assignments_failed', {
      campaign_id: campaignId,
      code: campaignAssignmentsResult.error.code ?? null,
      message: campaignAssignmentsResult.error.message ?? 'unknown',
    });
  }

  const normalizedShiftAssignments = ((shiftAssignmentsResult.error ? [] : shiftAssignmentsResult.data) || []).map(
    (assignment: any) => ({
      ...assignment,
      shift: assignment.shift_id ? shiftById.get(assignment.shift_id) || null : null,
    }),
  );
  const campaignAssignmentRows = (campaignAssignmentsResult.error ? [] : campaignAssignmentsResult.data) || [];

  if (campaignAssignmentsResult.error && normalizedShiftAssignments.length === 0) {
    throw campaignAssignmentsResult.error;
  }

  if ((campaignShiftsError || shiftAssignmentsResult.error) && campaignAssignmentRows.length === 0) {
    throw shiftAssignmentsResult.error || campaignShiftsError;
  }

  const fallbackRows = [
    ...buildFallbackShiftRows(normalizedShiftAssignments, orgId),
    ...buildFallbackCampaignAssignmentRows(campaignAssignmentRows),
  ];

  const enrichedRows = await enrichCampaignPromoterRows(fallbackRows);
  return mergeCampaignPromoterRows(enrichedRows);
};

export const fetchCampaignPromoters = async (
  campaignId: string,
  options?: { includeInvited?: boolean },
) => {
  const includeInvited = options?.includeInvited ?? true;

  const { data, error } = await supabaseAny.rpc('get_campaign_promoters', {
    p_campaign_id: campaignId,
    p_include_invited: includeInvited,
  });
  let mergedRows: CampaignPromoter[];

  if (error) {
    console.warn('[campaign-promoters]', {
      event: 'rpc_failed_falling_back_to_direct_queries',
      campaign_id: campaignId,
      include_invited: includeInvited,
      code: error.code ?? null,
      message: error.message ?? 'unknown',
    });
    mergedRows = await fetchCampaignPromotersFallback(campaignId, options);
  } else {
    const rows: CampaignPromoterRow[] = (data || []) as CampaignPromoterRow[];
    mergedRows =
      rows.length > 0 ? mergeCampaignPromoterRows(rows) : await fetchCampaignPromotersFallback(campaignId, options);
  }

  const baIds = Array.from(
    new Set(
      mergedRows
        .map((promoter) => promoter.ba_id)
        .filter((baId): baId is string => typeof baId === 'string' && baId.length > 0),
    ),
  );

  let mergedWithLiveStatus = mergedRows;
  if (baIds.length > 0) {
    const { data: liveShiftRows, error: liveShiftError } = await supabaseAny
      .from('shift_assignments')
      .select('ba_id, status, created_at, shift:campaign_shifts!inner(campaign_id)')
      .eq('shift.campaign_id', campaignId)
      .in('ba_id', baIds)
      .in('status', ['checked_in', 'completed']);

    if (liveShiftError) {
      console.warn('[campaign-promoters]', {
        event: 'live_shift_status_overlay_failed',
        campaign_id: campaignId,
        ba_count: baIds.length,
        code: liveShiftError.code ?? null,
        message: liveShiftError.message ?? 'unknown',
      });
    } else {
      const liveStatusByBa = new Map<string, CampaignPromoterStatus>();
      const liveStatusPriority: Record<CampaignPromoterStatus, number> = {
        declined: 0,
        invited: 0,
        accepted: 0,
        completed: 1,
        active: 2,
      };

      (liveShiftRows || []).forEach((row: any) => {
        const baId = typeof row?.ba_id === 'string' ? row.ba_id : null;
        if (!baId) return;

        const status =
          row?.status === 'checked_in'
            ? ('active' as CampaignPromoterStatus)
            : row?.status === 'completed'
              ? ('completed' as CampaignPromoterStatus)
              : null;
        if (!status) return;

        const existing = liveStatusByBa.get(baId);
        if (!existing || liveStatusPriority[status] > liveStatusPriority[existing]) {
          liveStatusByBa.set(baId, status);
        }
      });

      if (liveStatusByBa.size > 0) {
        mergedWithLiveStatus = mergedRows.map((promoter) => {
          const liveStatus = liveStatusByBa.get(promoter.ba_id);
          if (!liveStatus) return promoter;
          return { ...promoter, status: liveStatus };
        });
      }
    }
  }

  const shiftCountTotal = mergedWithLiveStatus.reduce(
    (sum, promoter) => sum + (promoter.shift_count || 0),
    0,
  );
  const assignmentRowsTotal = mergedWithLiveStatus.filter(
    (promoter) => promoter.has_campaign_assignment,
  ).length;

  console.info('[campaign-promoters]', {
    event: 'fetch_campaign_promoters',
    campaign_id: campaignId,
    include_invited: includeInvited,
    shift_count_total: shiftCountTotal,
    assignment_rows_total: assignmentRowsTotal,
    promoter_count_returned: mergedWithLiveStatus.length,
  });

  return mergedWithLiveStatus;
};

export const fetchBALinkedCampaignIds = async (
  baIdOrIds: string | string[],
  options?: { includeInvited?: boolean },
) => {
  const includeInvited = options?.includeInvited ?? true;
  const baIds = Array.from(
    new Set(
      (Array.isArray(baIdOrIds) ? baIdOrIds : [baIdOrIds]).filter(
        (baId): baId is string => typeof baId === 'string' && baId.length > 0,
      ),
    ),
  );

  if (baIds.length === 0) {
    return [];
  }

  const shiftStatuses = includeInvited
    ? [...campaignPromoterLinkedStatuses]
    : [...campaignPromoterCommittedStatuses];
  const campaignAssignmentStatuses = includeInvited
    ? ['invited', 'accepted', 'active', 'completed']
    : ['accepted', 'active', 'completed'];

  const [shiftLinksResult, assignmentLinksResult] = await Promise.all([
    supabaseAny
      .from('shift_assignments')
      .select('shift:campaign_shifts!inner(campaign_id)')
      .in('ba_id', baIds)
      .in('status', shiftStatuses),
    supabaseAny
      .from('campaign_assignments')
      .select('campaign_id')
      .in('ba_id', baIds)
      .in('status', campaignAssignmentStatuses),
  ]);

  if (shiftLinksResult.error) throw shiftLinksResult.error;
  if (assignmentLinksResult.error) throw assignmentLinksResult.error;

  const campaignIds = new Set<string>();

  (shiftLinksResult.data || []).forEach((row: any) => {
    const campaignId = row?.shift?.campaign_id;
    if (typeof campaignId === 'string' && campaignId.length > 0) {
      campaignIds.add(campaignId);
    }
  });

  (assignmentLinksResult.data || []).forEach((row: any) => {
    if (typeof row?.campaign_id === 'string' && row.campaign_id.length > 0) {
      campaignIds.add(row.campaign_id);
    }
  });

  const linkedCampaignIds = Array.from(campaignIds);

  console.info('[campaign-promoters]', {
    event: 'fetch_ba_linked_campaign_ids',
    ba_ids: baIds,
    ba_count: baIds.length,
    include_invited: includeInvited,
    shift_link_count: (shiftLinksResult.data || []).length,
    assignment_link_count: (assignmentLinksResult.data || []).length,
    campaign_count: linkedCampaignIds.length,
  });

  return linkedCampaignIds;
};
